import React, {useEffect, useState} from 'react';
import {Button, Modal, Select, TimePicker, Checkbox, Divider, Input, message, Table, Pagination} from 'antd';
import {Pageable, PageTestPlanDto, TestPlanControllerApi, TestPlanDto, TestPlanSchedulerControllerApi} from "../../../api";
import {useKeycloakHeader} from "../../../config/Config";
import {renderStatusAmpel} from "../../../utils/Utils";
import {TableRowSelection} from "antd/es/table/interface";

const {Option} = Select;


const CronModal = ({isOpen, onClose, onSave}) => {
    const [mode, setMode] = useState('minutes'); // Options: minutes, hourly, daily, weekly
    const [time, setTime] = useState<string | null>(null); // Uhrzeit für tägliche oder wöchentliche Modi
    const [hourInterval, setHourInterval] = useState('1'); // Stundenintervall für stündlichen Modus
    const [minuteInterval, setMinuteInterval] = useState('10'); // Minutenintervall für minütlichen Modus
    const [selectedDays, setSelectedDays] = useState<string[]>([]); // Ausgewählte Wochentage für wöchentlichen Modus
    const [isEveryDay, setIsEveryDay] = useState(false); // Täglich - ob jeder Tag oder nur bestimmte Tage
    const [customCronExpression, setCustomCronExpression] = useState<string>();

    // Neue Zustände für die Testplan-Suche
    const [searchTerm, setSearchTerm] = useState('');
    const [testPlans, setTestPlans] = useState<TestPlanDto[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(5);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [selectedTestPlan, setSelectedTestPlan] = useState<TestPlanDto | null>(null);


    const testPlanControllerApi = new TestPlanControllerApi();
    const testPlanSchedulerControllerApi = new TestPlanSchedulerControllerApi();
    const headers = useKeycloakHeader();

    useEffect(() => {
        fetchTestPlans(currentPage, pageSize, searchTerm);
    }, [currentPage, pageSize, searchTerm]);

    const fetchTestPlans = (page: number, size: number, search: string) => {
        const pageable: Pageable = {
            page: page - 1,
            size: size ? size : 5,
        } as Pageable;

        testPlanControllerApi.getAllTestPlansWithPageAnSearch(pageable, search, headers).then((response) => {
            const data: PageTestPlanDto = response.data;
            setTestPlans(data.content ? data.content : []);
            setTotalItems(data.totalElements ? data.totalElements : 0);
        })
    };


    const generateCronExpression = () => {
        if (mode === 'minutes') {
            return `0 */${minuteInterval} * * * ?`; // Alle X Minuten
        } else if (mode === 'hourly') {
            let [hour, minute] = (time?.split(':').map(part => parseInt(part, 10)) || [0, 0]);
            return `0 ${minute} ${hour}/${hourInterval} * * ?`
        } else if (mode === 'daily') {
            let [hour, minute] = (time?.split(':').map(part => parseInt(part, 10)) || [0, 0]);
            if (isEveryDay) {
                return `0 ${minute} ${hour} * * ?`; // Jeden Tag zu einer bestimmten Uhrzeit
            } else {
                return `0 ${minute} ${hour} ? * ${selectedDays.join(',')}`; // Bestimmte Wochentage
            }
        } else if (mode === 'custom') {
            if (isValidCron(customCronExpression)) {
                return customCronExpression;
            }
            message.error("Der Benutzerdefinierte Cron-Ausdruck ist nicht valide!");
            return undefined;

        }
    };

    function isValidCron(cronExpression) {
        // RegEx zur Überprüfung eines 5-teiligen oder 6-teiligen Cron-Ausdrucks
        const cronRegex = /^(\*|([0-5]?\d))( (\*|([0-5]?\d))){4}(\s(\*|([0-5]?\d))){0,1}$/;
        const cronParts = cronExpression.trim().split(/\s+/);

        // Überprüfen, ob der Ausdruck 5 oder 6 Teile enthält
        if (cronParts.length !== 5 && cronParts.length !== 6) {
            return false;
        }

        // Überprüfung des regulären Ausdrucks
        return cronRegex.test(cronExpression);
    }

    const handleSave = () => {
        const cronExpression = generateCronExpression();
        if(cronExpression){
            createNewScheduledTestPlanTask(cronExpression);
        }
    };

    const createNewScheduledTestPlanTask = (cronExpression: string) => {
        if(selectedTestPlan && selectedTestPlan.id){
            testPlanSchedulerControllerApi.scheduleTask(selectedTestPlan.id, cronExpression, headers).then((response) => {
                message.success("Es wurde eine neue Testplanung erstellt.");
                onSave();
                onClose();
            }).catch((error) => {
                message.error("Es ist ein Fehler bei der Erstellung einer neuen Testplanung aufgetreten!")
            })
        }

    }

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };


    // Konfiguration für die Zeilenauswahl
    const rowSelection: TableRowSelection<TestPlanDto> = {
        type: 'radio', // Nur eine Auswahl pro Tabelle
        onChange: (selectedKeys: React.Key[], selectedRows: TestPlanDto[]) => {
            setSelectedTestPlan(selectedRows.length === 1 ? selectedRows[0] : null); // Das ausgewählte Testplanobjekt speichern
        },
    };

    return (
        <Modal
            title="Cron Einstellungen"
            open={isOpen}
            width={800}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Abbrechen
                </Button>,
                <Button key="submit" type="primary" onClick={handleSave}>
                    Speichern
                </Button>,
            ]}
        >
            <div>

                {/* Suchfeld für Testpläne */}
                <div style={{marginBottom: 20}}>
                    <Input.Search
                        placeholder="Testplan suchen"
                        onSearch={(value) => setSearchTerm(value)}
                        enterButton
                    />
                </div>

                {/* Tabelle der Testpläne */}
                <Table
                    dataSource={testPlans}
                    rowKey="id"
                    rowSelection={rowSelection}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalItems,
                        showSizeChanger: false,
                    }}
                    locale={{emptyText: 'Keine Daten vorhanden'}}
                    onChange={handleTableChange}
                    columns={[
                        {title: 'ID', dataIndex: 'id', key: 'id'},
                        {title: 'Name', dataIndex: 'name', key: 'name'},
                        {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            width: '5%',
                            render: (text, record) => (
                                <span>{record.latestRunStatus ? renderStatusAmpel(record.latestRunStatus) : ""}</span>
                            ),
                        },
                    ]}
                />

                <Divider/>

                <h3>Modus wählen:</h3>
                <Select defaultValue="minutes" style={{width: '100%'}} onChange={setMode}>
                    <Option value="minutes">Minütlich</Option>
                    <Option value="hourly">Stündlich</Option>
                    <Option value="daily">Täglich</Option>
                    <Option value="custom">Benutzerdefiniert</Option>
                </Select>
                <Divider/>

                {/* Minuten Mode */}
                {mode === 'minutes' && (
                    <div>
                        <span>Alle </span>
                        <Select defaultValue="10" style={{width: 200}} onChange={setMinuteInterval}>
                            <Option value="5">5</Option>
                            <Option value="10">10</Option>
                            <Option value="15">15</Option>
                            <Option value="20">20</Option>
                            <Option value="30">30</Option>
                        </Select>
                        <span> Minute(n)</span>
                    </div>
                )}

                {/* Stündlich Mode */}
                {mode === 'hourly' && (
                    <div>
                        <span>Alle </span>
                        <Select defaultValue="1" style={{width: 200}} onChange={setHourInterval}>
                            <Option value="1">1</Option>
                            <Option value="2">2</Option>
                            <Option value="3">3</Option>
                            <Option value="4">4</Option>
                            <Option value="6">6</Option>
                            <Option value="12">12</Option>
                        </Select>
                        <span> Stunde(n) </span>
                        <br/>
                        <div style={{marginTop: 10}}>
                            <span>Starten ab </span>
                            <TimePicker
                                placeholder={'Uhrzeit'}
                                format="HH:mm"
                                onChange={(time, timeString) => {
                                    setTime(Array.isArray(timeString) ? timeString[0] : timeString);
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Daily Mode */}
                {mode === 'daily' && (
                    <div>
                        <h3>Wiederholung:</h3>
                        <Checkbox
                            checked={isEveryDay}
                            onChange={(e) => setIsEveryDay(e.target.checked)}
                        >
                            Jeden Tag
                        </Checkbox>
                        {!isEveryDay && (
                            <>
                                <h3>Wochentage wählen:</h3>
                                <Checkbox.Group
                                    options={[
                                        {label: 'Montag', value: '2'},
                                        {label: 'Dienstag', value: '3'},
                                        {label: 'Mittwoch', value: '4'},
                                        {label: 'Donnerstag', value: '5'},
                                        {label: 'Freitag', value: '6'},
                                        {label: 'Samstag', value: '7'},
                                        {label: 'Sonntag', value: '1'},
                                    ]}
                                    onChange={setSelectedDays}
                                />
                            </>
                        )}
                        <Divider/>
                        <h3>Zeit wählen:</h3>
                        <span>Ausführung um </span>
                        <TimePicker
                            format="HH:mm"
                            placeholder={'Uhrzeit'}
                            onChange={(time, timeString) => {
                                setTime(Array.isArray(timeString) ? timeString[0] : timeString);
                            }}
                        />
                    </div>
                )}

                {/* Weekly Mode */}
                {mode === 'custom' && (
                    <div>
                        <h3>Benutzerdefinierten Cron-Ausdruck:</h3>
                        <Input onChange={(e) => setCustomCronExpression(e.target.value)}
                               placeholder="Cron-Ausdruck"/>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default CronModal;
